import React from 'react';
import {
  Box,
  Paper,
  Typography
} from '@mui/material';
import MuiGrid from '../MuiGrid';
import PlayerCircle, { PlayerState } from '../simulator/PlayerCircle';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PanToolIcon from '@mui/icons-material/PanTool';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface ReadOnlySimulatorProps {
  playerStates: PlayerState[];
}

const ReadOnlySimulator: React.FC<ReadOnlySimulatorProps> = ({ playerStates }) => {
  // Calculate positions in a horseshoe arrangement with a gap at the top (same as MafiaSimulator)
  const calculatePosition = (index: number, total: number, radius: number) => {
    const startAngle = -5 * Math.PI / 12;
    const endAngle = 5 * Math.PI / 12 + Math.PI;
    const arcAngle = endAngle - startAngle;
    const angleStep = arcAngle / (total - 1);
    const angle = startAngle + index * angleStep;

    return {
      left: radius + radius * Math.cos(angle),
      top: radius + radius * Math.sin(angle)
    };
  };

  // Empty handlers for read-only mode
  const handlePlayerClick = () => {};
  const handlePlayerRightClick = () => {};

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h3" gutterBottom sx={{ textAlign: 'center' }}>
          Game State
        </Typography>
      </Box>

      {/* Game board with players in a circle */}
      <Box
        sx={{
          position: 'relative',
          height: '450px',
          width: '450px',
          mt: 2,
          mb: 2
        }}
      >
        {playerStates.map((player, index) => {
          const { left, top } = calculatePosition(index, playerStates.length, 190);
          return (
            <Box
              key={player.id}
              sx={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none' // Disable all interactions
              }}
            >
              <PlayerCircle
                player={player}
                onClick={handlePlayerClick}
                onRightClick={handlePlayerRightClick}
                size={65}
              />
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2, background: 'rgba(0,0,0,0.05)', width: '100%', maxWidth: '600px' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>Legend:</Typography>
        <MuiGrid container spacing={2} sx={{ justifyContent: 'center' }}>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#e57373' }} />
              <Typography variant="body2">Civilian</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#424242', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiberManualRecordIcon style={{ color: 'white', fontSize: '10px' }} />
              </Box>
              <Typography variant="body2">Mafia</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#212121', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PanToolIcon style={{ color: 'white', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Don</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#c62828', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocalPoliceIcon style={{ color: 'white', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Sheriff</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#c8b3c4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HelpOutlineIcon style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '12px' }} />
              </Box>
              <Typography variant="body2">Unknown</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: '#e57373',
                opacity: 0.4
              }} />
              <Typography variant="body2">Dead Player</Typography>
            </Box>
          </MuiGrid>
          <MuiGrid item xs={6} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: '#e57373',
                border: '2px dashed #ffd700'
              }} />
              <Typography variant="body2">Checked by Sheriff</Typography>
            </Box>
          </MuiGrid>

          <MuiGrid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                padding: '1px 4px',
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                ➔ 2, 5
              </Box>
              <Typography variant="body2">Player's votes (shown below player)</Typography>
            </Box>
          </MuiGrid>
        </MuiGrid>
      </Paper>
    </Paper>
  );
};

export default ReadOnlySimulator;
